import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, BookOpen, Mic, Heart, Sparkles, ArrowRight, Box } from "lucide-react";
import { Button } from "@/components/ui/button";

const features = [
  { icon: Shield, title: "Private Vault", desc: "Safely store photos, videos, voice notes, and documents in your encrypted digital vault." },
  { icon: BookOpen, title: "Journal", desc: "Write daily journal entries and capture your thoughts, feelings, and life stories." },
  { icon: Mic, title: "AI Voice Chat", desc: "Talk to your AI companion who remembers every conversation and helps you reflect." },
  { icon: Sparkles, title: "Daily Prompts", desc: "Deep questions that inspire you to capture memories you never knew you wanted to keep." },
  { icon: Heart, title: "Legacy Messages", desc: "Create sealed messages for loved ones, delivered only when the time is right." },
  { icon: Shield, title: "Will & Access", desc: "Authorize a trusted person to receive admin rights to your vault when you pass." },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] } }),
};

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#000000] text-white" data-testid="landing-page">
      {/* Nav */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/[0.06] bg-[#000000]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#00FF88] flex items-center justify-center shadow-lg shadow-[#00FF88]/20">
              <Box className="w-4 h-4 text-black" />
            </div>
            <span className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Black Box</span>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/auth")}
              data-testid="landing-login-btn"
              className="text-slate-400 hover:text-white"
            >
              Sign In
            </Button>
            <Button
              onClick={() => navigate("/auth")}
              data-testid="landing-get-started-btn"
              className="bg-[#00FF88] text-black hover:bg-[#00CC6A] rounded-full px-6 font-medium"
            >
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-16">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1772489678214-3375ea5f267d?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#000000] via-[#000000]/80 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#000000] via-transparent to-[#000000]/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24">
          <motion.div
            initial="hidden"
            animate="visible"
            className="max-w-3xl"
          >
            <motion.p
              custom={0}
              variants={fadeUp}
              className="text-xs tracking-[0.3em] uppercase text-[#00FF88] mb-6 font-medium"
            >
              Your Digital Legacy
            </motion.p>
            <motion.h1
              custom={1}
              variants={fadeUp}
              className="text-5xl md:text-7xl font-bold tracking-tight leading-none mb-8"
              style={{ fontFamily: 'Manrope' }}
            >
              Your Life.<br />
              <span className="text-[#00FF88]">Preserved Forever.</span>
            </motion.h1>
            <motion.p
              custom={2}
              variants={fadeUp}
              className="text-base md:text-lg text-slate-300 leading-relaxed max-w-xl mb-12"
            >
              The digital vault for your most precious memories, stories, and messages
              to the people who matter most. Every voice note, every journal entry,
              every photo - safely preserved for eternity.
            </motion.p>
            <motion.div custom={3} variants={fadeUp} className="flex flex-wrap gap-4">
              <Button
                onClick={() => navigate("/auth")}
                data-testid="hero-get-started-btn"
                className="bg-[#00FF88] text-black hover:bg-[#00CC6A] rounded-full px-8 py-6 text-base font-bold shadow-[0_0_30px_rgba(0,255,136,0.3)] hover:scale-105 active:scale-95 transition-transform duration-200 neon-btn-pulse"
              >
                Start Preserving
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="hero-learn-more-btn"
                className="bg-white/5 border-white/10 text-white hover:bg-white/10 rounded-full px-8 py-6 text-base font-medium backdrop-blur-md"
              >
                Learn More
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-32 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-20"
          >
            <p className="text-xs tracking-[0.3em] uppercase text-[#00FF88] mb-4 font-medium">Features</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope' }}>
              Everything you need to<br />preserve what matters
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-8 hover:border-white/[0.15] transition-all duration-500 group shadow-[0_8px_32px_rgba(0,0,0,0.3)]"
              >
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-6 group-hover:border-[#00FF88]/30 transition-colors duration-500">
                  <f.icon className="w-5 h-5 text-[#00FF88]" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-semibold mb-3 text-white" style={{ fontFamily: 'Manrope' }}>{f.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-32 border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-20"
          >
            <p className="text-xs tracking-[0.3em] uppercase text-[#00FF88] mb-4 font-medium">How It Works</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope' }}>
              Three simple steps
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { step: "01", title: "Create Your Vault", desc: "Sign up and build your private profile. Your digital sanctuary awaits." },
              { step: "02", title: "Capture Daily", desc: "Upload memories, respond to daily prompts, chat with your AI companion." },
              { step: "03", title: "Set Your Legacy", desc: "Designate beneficiaries and create sealed messages for your loved ones." },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
              >
                <span className="text-6xl font-bold text-white/5 block mb-4" style={{ fontFamily: 'Manrope' }}>{s.step}</span>
                <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Manrope' }}>{s.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Manrope' }}>
              Start your legacy today
            </h2>
            <p className="text-slate-400 text-base md:text-lg max-w-lg mx-auto mb-10">
              Every day is a chance to preserve a memory. Don't let another one slip away.
            </p>
            <Button
              onClick={() => navigate("/auth")}
              data-testid="cta-get-started-btn"
              className="bg-[#00FF88] text-black hover:bg-[#00CC6A] rounded-full px-10 py-6 text-base font-bold shadow-[0_0_30px_rgba(0,255,136,0.3)] hover:scale-105 active:scale-95 transition-transform duration-200"
            >
              Get Started Free
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/[0.06] py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Box className="w-4 h-4 text-[#00FF88]" />
            <span className="text-sm text-slate-500">Black Box</span>
          </div>
          <p className="text-xs text-slate-600">Your memories, preserved forever.</p>
        </div>
      </footer>
    </div>
  );
}
